import { button, element, heading } from "@groundspark/browser-ui";
import { coverageChart, historyChart } from "./activity-charts";
import type { BranchStatistics, LeafletScanTotal } from "./statistics";

const HISTORY_TITLE = "Daily activity · last 3 months";
const numbers = new Intl.NumberFormat("en-GB");

export function statisticsPanel(
  statistics: BranchStatistics,
  refresh: () => Promise<void>,
  busy: boolean,
): HTMLElement {
  const leafleting = element(
    "article",
    "activity-column activity-column--leafleting",
    heading(3, "Leafleting", "activity-column__title"),
    element(
      "div",
      "activity-coverage",
      element(
        "div",
        "activity-pie-placeholder",
        element("span", null, "Street coverage"),
        element("span", "muted", "Coming soon"),
      ),
      element(
        "div",
        "activity-coverage__copy",
        element("p", "activity-total", numbers.format(statistics.leafleting.completed)),
        element("p", null, "streets leafleted"),
        element("p", "muted", "A total street target is needed to show coverage."),
      ),
    ),
    historyChart(
      statistics.history.map((day) => ({ date: day.date, count: day.streetCount })),
      { title: HISTORY_TITLE, unit: "streets", measure: "streets leafleted" },
    ),
  );
  const canvassing = element(
    "article",
    "activity-column activity-column--canvassing",
    heading(3, "Canvassing", "activity-column__title"),
    coverageChart(
      statistics.canvassing.completed,
      statistics.canvassing.total,
      "voters",
      "canvassed",
    ),
    historyChart(
      statistics.history.map((day) => ({ date: day.date, count: day.electorCount })),
      { title: HISTORY_TITLE, unit: "voters", measure: "voters canvassed" },
    ),
  );
  return element(
    "div",
    null,
    element(
      "header",
      "panel-heading statistics-heading",
      element(
        "div",
        null,
        heading(2, "Campaign statistics", "panel-heading__title"),
        element("p", "muted", "First completions in this branch’s accessible projects."),
      ),
      button("Refresh", "button button--white", refresh, busy),
    ),
    element("div", "activity-columns", leafleting, canvassing),
    element(
      "p",
      "statistics-note muted",
      "Each voter or street counts once per project. Voter coverage uses current electoral rolls; history includes previous roll versions. Edits and undone completions do not reduce activity counts.",
    ),
    leafletScans(statistics.leafletScans),
  );
}

/** Ranked bars compare the leaflets; each row also gives its name and count as text. */
function leafletScans(leaflets: readonly LeafletScanTotal[]): HTMLElement {
  const section = element(
    "section",
    "leaflet-scans",
    heading(3, "Leaflet scans", "leaflet-scans__title"),
    element(
      "p",
      "muted",
      "QR code scans since each leaflet was created, most scanned first. Repeat scans count each time.",
    ),
  );
  if (leaflets.length === 0) {
    section.append(element("p", "muted", "No leaflets yet."));
    return section;
  }
  const most = Math.max(1, ...leaflets.map((leaflet) => leaflet.scans));
  const list = element("ol", "leaflet-scans__list");
  for (const { name, scans } of leaflets) {
    const bar = element("span", "leaflet-scans__bar");
    bar.style.width = `${(scans / most) * 100}%`;
    const track = element("span", "leaflet-scans__track", bar);
    track.setAttribute("aria-hidden", "true");
    const row = element(
      "li",
      "leaflet-scans__row",
      element("span", "leaflet-scans__name", name),
      track,
      element("span", "leaflet-scans__count", numbers.format(scans)),
    );
    row.title = `${name}: ${numbers.format(scans)} ${scans === 1 ? "scan" : "scans"}`;
    list.append(row);
  }
  section.append(list);
  return section;
}
